import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { userAtom } from '../../../recoil/atoms';
import { getGoogleUserInfo, loginLearnerWithGoogle } from '../../../services/loginApi';

const Button = ({ onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const setUser = useSetRecoilState(userAtom);

  const handleSuccess = async (tokenResponse) => {
    setIsLoading(true);
    if (onError) onError('');

    try {
      console.log('🔑 Step 1-3: Google OAuth successful, access_token received');
      
      const accessToken = tokenResponse.access_token;
      
      // Step 4: Get user info from Google API
      console.log('📡 Step 4: Fetching user info from Google API...');
      const googleUserInfoResponse = await getGoogleUserInfo(accessToken);
      
      if (!googleUserInfoResponse.success) {
        throw new Error(googleUserInfoResponse.message || 'Failed to get user info from Google');
      }
      
      const googleUserInfo = googleUserInfoResponse.data;
      console.log('✅ Step 4 Complete: Google user info retrieved', { 
        email: googleUserInfo.email, 
        name: googleUserInfo.name 
      });
      
      // Step 5: Send to backend learner-google API
      console.log('📡 Step 5: Sending login request to backend...');
      const loginResponse = await loginLearnerWithGoogle({
        accessToken: accessToken,
        email: googleUserInfo.email,
        name: googleUserInfo.name,
      });
      
      if (!loginResponse.success) {
        console.error('❌ Step 5 Failed:', loginResponse.message);
        throw new Error(loginResponse.message || 'Login failed. Please try again.');
      }
      
      console.log('✅ Step 5-6 Complete: Backend validation successful, JWT token received');
      
      // Step 7: Save JWT token + user data to localStorage
      const backendUserData = loginResponse.data['user-data'];
      const jwtToken = loginResponse.data.token;
      
      const userData = {
        ...backendUserData,
        picture: googleUserInfo.picture, // Save picture for UI
        name: backendUserData.name || googleUserInfo.name,
      };
      
      // Save universal-learning-id to localStorage if available
      const universalLearningId = backendUserData['universal-learning-id'] || 
                                   backendUserData.universalLearningId ||
                                   backendUserData['universal_learning_id'];
      if (universalLearningId) {
        localStorage.setItem('universal-learning-id', universalLearningId);
        console.log('💾 Saved universal-learning-id to localStorage:', universalLearningId);
      }
      
      setUser(userData);
      localStorage.setItem('token', jwtToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      console.log('💾 Step 7 Complete: JWT token & user data saved to localStorage');
      
      // Step 8: Redirect to dashboard
      console.log('🚀 Step 8: Redirecting to dashboard...');
      navigate('/learner/dashboard');
      
    } catch (err) {
      console.error('❌ Error during login:', err);
      if (onError) {
        onError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = () => {
    if (onError) {
      onError('Google Sign-In failed. Please try again.');
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: handleError,
    scope: 'openid email profile',
  });

  return (
    <div className="w-full flex items-center justify-center">
      <button
        className="w-full flex items-center justify-center gap-3 bg-white rounded-xl shadow-md border-2 border-gray-200 py-3.5 px-6 transition-all duration-300 ease-out cursor-pointer text-base font-medium text-[#1a1a2e] hover:enabled:shadow-lg hover:enabled:border-gray-300 hover:enabled:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
        onClick={() => googleLogin()}
        disabled={isLoading}
      >
        <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span className="font-medium">
          {isLoading ? 'Signing in...' : 'Continue with Google'}
        </span>
      </button>
    </div>
  );
};

export default Button;

